// ============================================
// PWA Install Prompt Component
// ============================================

import React from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallPromptProps {
  variant?: 'banner' | 'compact';
}

export default function InstallPrompt({ variant = 'banner' }: InstallPromptProps) {
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = React.useState(false);

  React.useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone === true;

    if (dismissed || isStandalone) {
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    if (iOS) {
      // Show iOS instructions after a delay
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  if (!showPrompt) return null;

  // iOS Instructions Modal
  if (isIOS && showIOSInstructions) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}>
        <div style={{
          backgroundColor: 'var(--color-bg, #1f2937)',
          borderRadius: '16px',
          padding: '1.5rem',
          maxWidth: '320px',
          width: '100%',
        }}>
          <h3 style={{
            margin: '0 0 1rem',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-text, #fff)',
          }}>
            Add to Home Screen
          </h3>
          <div style={{ color: 'var(--color-text-secondary, #9ca3af)', fontSize: '0.875rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              <strong>1.</strong> Tap the <strong>Share</strong> button
              <span style={{ display: 'inline-block', marginLeft: '0.5rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </span>
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong>2.</strong> Scroll down and tap <strong>"Add to Home Screen"</strong>
            </p>
            <p>
              <strong>3.</strong> Tap <strong>"Add"</strong> in the top right
            </p>
          </div>
          <button
            onClick={() => {
              setShowIOSInstructions(false);
              handleDismiss();
            }}
            style={{
              marginTop: '1.5rem',
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#22c55e',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Compact variant (small floating button)
  if (variant === 'compact') {
    return (
      <button
        onClick={isIOS ? handleShowIOSInstructions : handleInstall}
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#22c55e',
          color: '#fff',
          border: 'none',
          borderRadius: '50px',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 1000,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Install App
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          style={{
            marginLeft: '0.25rem',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            padding: '0 0.25rem',
            fontSize: '1.25rem',
            lineHeight: 1,
          }}
        >
          &times;
        </button>
      </button>
    );
  }

  // Banner variant (bottom banner)
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'var(--color-bg-secondary, #1f2937)',
      borderTop: '1px solid var(--color-border, #374151)',
      padding: '1rem',
      paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: 600,
          color: 'var(--color-text, #fff)',
          marginBottom: '0.25rem',
          fontSize: '0.9375rem',
        }}>
          Add to Home Screen
        </div>
        <div style={{
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary, #9ca3af)',
        }}>
          Quick access with app icon
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={handleDismiss}
          style={{
            padding: '0.625rem 1rem',
            backgroundColor: 'transparent',
            color: 'var(--color-text-secondary, #9ca3af)',
            border: '1px solid var(--color-border, #374151)',
            borderRadius: '8px',
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          Later
        </button>
        <button
          onClick={isIOS ? handleShowIOSInstructions : handleInstall}
          style={{
            padding: '0.625rem 1rem',
            backgroundColor: '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Install
        </button>
      </div>
    </div>
  );
}
